import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, MapPin, Globe, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { api, imgUrl, errMsg } from "@/lib/api";
import { usePageMeta } from "@/components/Share";
import { DemoBadge, FoundingBadge } from "@/components/MarketBits";
import NotFound from "@/pages/NotFound";

const field = "w-full bg-white border border-[#e2dcd4] px-4 py-3 text-[15px] focus:outline-none focus:border-[#1f402b]";

function MessageForm({ item }) {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [state, setState] = useState("idle");
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    setState("sending");
    try {
      const { data } = await api.post(`/market/listings/${item.id}/message`, form);
      setState(data.demo ? "demo" : "sent");
    } catch (err) {
      toast.error(errMsg(err));
      setState("idle");
    }
  };
  if (state === "sent" || state === "demo")
    return (
      <div className="border border-[#e2dcd4] bg-white p-6" data-testid="market-message-success">
        <CheckCircle2 className="text-[#3d644e]" />
        <p className="mt-3 text-[#1f402b] text-lg">Message sent.</p>
        <p className="mt-1 text-sm text-[#5a665e]">
          {state === "demo" ? "This is a demo advert, so no real seller will receive it." : "The seller will reply to your email."}
        </p>
      </div>
    );
  return (
    <form onSubmit={submit} className="border border-[#e2dcd4] bg-white p-6 space-y-3" data-testid="market-message-form">
      <p className="eyebrow">Message the seller</p>
      <input required placeholder="Your name" value={form.name} onChange={set("name")} className={field} data-testid="market-message-name" />
      <input required type="email" placeholder="Your email (only the seller sees it)" value={form.email} onChange={set("email")} className={field} data-testid="market-message-email" />
      <textarea required minLength={5} rows={4} placeholder="Your question" value={form.message} onChange={set("message")} className={field} data-testid="market-message-text" />
      <button disabled={state === "sending"} className="w-full bg-[#1f402b] text-white py-3 text-sm hover:bg-[#2c5a3d] disabled:opacity-60" data-testid="market-message-submit">
        {state === "sending" ? "Sending…" : "Send message"}
      </button>
    </form>
  );
}

export default function MarketListing() {
  const { id } = useParams();
  const [item, setItem] = useState(undefined);
  const [img, setImg] = useState(0);
  useEffect(() => {
    api.get(`/market/listings/${id}`).then(({ data }) => setItem(data)).catch(() => setItem(null));
  }, [id]);
  usePageMeta(item?.title, item?.description?.slice(0, 150));

  if (item === undefined) return <p className="max-w-7xl mx-auto px-8 pt-20 text-sm text-[#5a665e]">Loading…</p>;
  if (item === null) return <NotFound />;
  const demo = item.is_demo || item.seller?.is_demo;
  const s = item.seller || {};

  return (
    <div className="max-w-7xl mx-auto px-5 sm:px-8 pt-10 sm:pt-14" data-testid="market-listing-page">
      <Link to="/market" className="inline-flex items-center gap-2 text-sm text-[#5a665e] hover:text-[#1f402b]" data-testid="market-back-link"><ArrowLeft size={14} /> Back to market</Link>
      <div className="mt-8 grid lg:grid-cols-12 gap-10">
        <div className="lg:col-span-7">
          <div className="relative aspect-[4/3] bg-[#f4f0ea] overflow-hidden">
            {item.images?.[img] && <img src={imgUrl(item.images[img])} alt={item.title} className="w-full h-full object-cover" />}
            {demo && <DemoBadge className="absolute top-4 left-4" />}
          </div>
          {item.images?.length > 1 && (
            <div className="mt-3 flex gap-2">
              {item.images.map((u, i) => (
                <button key={u} onClick={() => setImg(i)} className={`w-20 h-16 overflow-hidden border ${i === img ? "border-[#1f402b]" : "border-transparent"}`} data-testid={`market-thumb-${i}`}>
                  <img src={imgUrl(u)} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>
        <div className="lg:col-span-5 space-y-6">
          <div>
            <p className="eyebrow">{item.category} · {item.kind === "service" ? "Service" : "Product"}</p>
            <h1 className="mt-3 text-3xl sm:text-4xl leading-tight tracking-tight text-[#1a261f]" data-testid="market-listing-title">{item.title}</h1>
            {item.price && <p className="mt-4 font-display text-3xl text-[#1f402b]" data-testid="market-listing-detail-price">{item.price}</p>}
          </div>
          <p className="text-[#3c4a41] leading-relaxed whitespace-pre-line" data-testid="market-listing-description">{item.description}</p>
          <div className="border-t border-[#e2dcd4] pt-5 text-sm space-y-1.5" data-testid="market-seller-info">
            <p className="text-[#1a261f] text-base">{s.business_name}</p>
            {(demo || s.founding_seller) && <FoundingBadge className="mt-1" />}
            {(item.region || s.region) && <p className="flex items-center gap-1.5 text-[#5a665e]"><MapPin size={13} /> {item.region || s.region}</p>}
            {s.website && <a href={s.website} target="_blank" rel="noopener noreferrer nofollow" className="flex items-center gap-1.5 text-[#3d644e] underline" data-testid="market-seller-website"><Globe size={13} /> Seller's website</a>}
          </div>
          <MessageForm item={item} />
          <p className="font-mono-k text-[11px] text-[#5a665e] leading-relaxed">kiwibares.nz checks adverts but doesn't sell these items. Deal directly with the seller.</p>
        </div>
      </div>
    </div>
  );
}
